// pages/ConjugationExercise.jsx
// Page d'exercices de conjugaison interactive (VERSION AMÉLIORÉE)

import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Dumbbell, Loader2, CheckCircle, XCircle, TrendingUp, RotateCw, BookOpen, List, X, Info } from 'lucide-react';

// Import des utilitaires partagés
import { getLanguageOptions, getLangueFlag, createInsertSpecialChar } from '../utils/exerciseUtils';
import VirtualKeyboard from '../components/VirtualKeyboard';
import { useLangueEtudiee } from '../hooks/useLangueEtudiee';
import { useLangueInterface } from '../contexts/LangueInterfaceContext';

import { useAuth } from '../contexts/AuthContext';
import './ExerciseShared.css';
import './ConjugationExercise.css';

export default function ConjugationExercise() {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const userId = user?.id || 1; // Fallback userId=1 pour embed sans auth
  const langueEtudiee = useLangueEtudiee();
  const { langueInterface } = useLangueInterface();
  const isEmbed = searchParams.get('embed') === 'true';
  const [loading, setLoading] = useState(true);
  const [question, setQuestion] = useState(null);
  const [userAnswer, setUserAnswer] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [stats, setStats] = useState({ correct: 0, incorrect: 0, total: 0 });
  const [error, setError] = useState(null);

  // Nouveaux états pour les filtres et modales
  // Charger les paramètres sauvegardés ou valeurs par défaut
  const savedParams = JSON.parse(localStorage.getItem('conjugationParams') || '{}');

  // langueEtudiee (param WP) prioritaire sur la préférence sauvegardée
  const [languageFilter, setLanguageFilter] = useState(langueEtudiee || savedParams.languageFilter || 'all');
  const [tenseFilter, setTenseFilter] = useState(savedParams.tenseFilter || 'random');
  const [difficultyMode, setDifficultyMode] = useState(savedParams.difficultyMode || 'normal');
  const [verbTypeFilter, setVerbTypeFilter] = useState(savedParams.verbTypeFilter || 'tous');

  const [showTenseExplanation, setShowTenseExplanation] = useState(false);
  const [checkAccents, setCheckAccents] = useState(false); // Vérification des accents (décochée par défaut)
  const [showConjugations, setShowConjugations] = useState(false);
  const [currentConjugations, setCurrentConjugations] = useState(null);
  const [loadingConjugations, setLoadingConjugations] = useState(false);

  // État pour les informations sur le verbe
  const [showVerbInfo, setShowVerbInfo] = useState(false);
  const [currentVerbInfo, setCurrentVerbInfo] = useState(null);
  const [loadingVerbInfo, setLoadingVerbInfo] = useState(false);

  // État pour les temps disponibles (chargés depuis l'API)
  const [tenseOptions, setTenseOptions] = useState([{ value: 'random', label: 'Tous les temps', niveau: 0 }]);
  const [userNiveau, setUserNiveau] = useState(0); // Niveau de l'utilisateur

  // État pour l'explication du temps actuel
  const [currentTenseExplanation, setCurrentTenseExplanation] = useState(null);
  const [loadingExplanation, setLoadingExplanation] = useState(false);

  // Refs pour les sélecteurs et l'input
  const languageSelectRef = useRef(null);
  const tenseSelectRef = useRef(null);
  const exerciseRef = useRef(null);
  const inputRef = useRef(null); // Ref pour l'input de réponse

  // État pour afficher/masquer l'exercice
  const [showExercise, setShowExercise] = useState(false);

  // Réinitialiser l'écran 1 quand on navigue vers cette page depuis le menu
  useEffect(() => {
    if (location.state?.resetToScreen1) {
      setShowExercise(false);
      setQuestion(null);
      setUserAnswer('');
      setResult(null);
    }
  }, [location.state]);

  // Charger les temps disponibles depuis l'API
  useEffect(() => {
    if (!userId) return;

    const loadAvailableTenses = async () => {
      try {
        // Déterminer la langue cible pour l'API
        let langueCible = languageFilter;
        if (languageFilter === 'all') {
          langueCible = 'fr'; // Par défaut français si "Toutes langues"
        }


        const url = `/api/exercices/temps-disponibles?userId=${userId}&langueCible=${langueCible}&mode=${difficultyMode}&langueExplication=${langueInterface}`;

        console.log('🔄 Chargement temps:', {
          langueCible,
          mode: difficultyMode,
        });

        const response = await fetch(url);
        const data = await response.json();

        console.log('✅ Réponse API:', data);

        if (data.success && data.data && data.data.temps) {
          // Afficher le warning s'il existe
          if (data.warning) {
            console.warn('⚠️', data.warning);
            alert(data.warning);
          }

          setTenseOptions(data.data.temps);
          setUserNiveau(data.data.niveauUtilisateur || 0);

          console.log(`📊 ${data.data.temps.length} temps chargés pour ${langueCible}`);

          // Réinitialiser le filtre de temps si le temps sélectionné n'est plus disponible
          const tempsDisponibles = data.data.temps.map((t) => t.value);
          if (tenseFilter !== 'random' && !tempsDisponibles.includes(tenseFilter)) {
            console.log('⚠️ Temps sélectionné non disponible, réinitialisation');
            setTenseFilter('random');
          }
        } else {
          console.error('❌ Réponse API invalide:', data);
          // En cas de réponse invalide, garder "Tous les temps"
          setTenseOptions([{ value: 'random', label: 'Tous les temps', niveau: 0 }]);
        }
      } catch (error) {
        console.error('❌ Erreur chargement temps disponibles:', error);
        // En cas d'erreur, garder au minimum "Tous les temps"
        setTenseOptions([{ value: 'random', label: 'Tous les temps', niveau: 0 }]);
      }
    };

    loadAvailableTenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, languageFilter, difficultyMode, langueInterface]); // Recharger quand user, langue OU mode change

  // Fonction pour détecter si verbe irrégulier
  const isVerbIrregular = (verbInfo) => {
    if (!verbInfo || !verbInfo.attributes) return false;
    return verbInfo.attributes.some((attr) => attr.type === 'irregularite' || attr.important === true);
  };

  // Fonction pour générer les options selon la langue
  // Fonction pour générer les options selon la langue
  const getVerbTypeOptions = (langue) => {
    const baseOptions = [{ value: 'tous', label: t('conjugation.verbeTous') }];

    // Protection si langue undefined/null
    if (!langue) {
      return baseOptions;
    }

    if (langue === 'es') {
      return [
        ...baseOptions,
        { value: 'regulier', label: t('conjugation.verbeRegulier') },
        { value: 'diphtongue_e_ie', label: t('conjugation.verbeDiphtongueEIe') },
        { value: 'diphtongue_o_ue', label: t('conjugation.verbeDiphtongueOUe') },
        { value: 'alternance_e_i', label: t('conjugation.verbeAlternanceEI') },
        { value: 'irregulier_complet', label: t('conjugation.verbeIrregulierComplet') },
      ];
    }

    if (langue === 'fr') {
      return [
        ...baseOptions,
        { value: 'regulier', label: t('conjugation.verbeRegulierFrEr') },
        { value: 'irregulier', label: t('conjugation.verbeIrregulierFr') },
      ];
    }

    if (langue === 'en') {
      return [...baseOptions, { value: 'regulier', label: t('conjugation.verbeRegulierEn') }, { value: 'irregulier', label: t('conjugation.verbeIrregulier') }];
    }

    // Langue = "all" ou autre
    return [...baseOptions, { value: 'regulier', label: t('conjugation.verbeRegulier') }, { value: 'irregulier', label: t('conjugation.verbeIrregulier') }];
  };

  // Charger une nouvelle question
  // Fonction pour re-tester le même verbe (garder la question, réinitialiser la réponse)
  const retryCurrentVerb = () => {
    setResult(null);
    setUserAnswer('');

    setTimeout(() => {
      const input = document.querySelector('.answer-input');
      if (input) input.focus();
    }, 100);
  };

  const loadNewQuestion = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    setUserAnswer('');

    try {
      // Construire l'URL avec le filtre de langue cible
      // Ajouter un timestamp pour éviter le cache et forcer un nouveau verbe
      let url = `/api/exercices/conjugaison/random?userId=${userId}&_t=${Date.now()}&langueExplication=${langueInterface}`;

      if (languageFilter !== 'all') {
        url += `&langueCible=${languageFilter}`;
      }

      // Ajouter le filtre de temps
      if (tenseFilter !== 'random') {
        url += `&temps=${tenseFilter}`;
      }

      if (verbTypeFilter !== 'tous') {
        url += `&typeIrregularite=${verbTypeFilter}`;
      }
      const response = await fetch(url);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Erreur lors du chargement');
      }

      setQuestion(data.data);
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Charger automatiquement les infos du verbe quand la question change
  useEffect(() => {
    if (question && question.verbeId) {
      loadVerbInfoSilent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question?.verbeId]); // Dépendance sur l'ID uniquement, pas l'objet entier

  // Focus initial sur le sélecteur de langue
  useEffect(() => {
    if (languageSelectRef.current) {
      languageSelectRef.current.focus();
    }
  }, []); // Seulement au montage initial

  // Sauvegarder les paramètres dans localStorage
  useEffect(() => {
    const params = {
      languageFilter,
      tenseFilter,
      difficultyMode,
      verbTypeFilter,
    };
    localStorage.setItem('conjugationParams', JSON.stringify(params));
  }, [languageFilter, tenseFilter, difficultyMode, verbTypeFilter]);

  // Vérifier la réponse
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🔵 SUBMIT - Début');

    if (!userAnswer.trim()) return;
    setIsChecking(true);
    console.log('🔵 SUBMIT - isChecking = true');

    try {
      console.log('🔵 SUBMIT - Avant fetch');
      const response = await fetch('/api/exercices/conjugaison/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          verbeId: question.verbeId,
          temps: question.temps,
          personne: question.personne,
          reponseUtilisateur: userAnswer,
          reponseCorrecte: question.reponseCorrecte,
          userId: userId,
          checkAccents: checkAccents, // Envoyer le flag de vérification des accents
        }),
      });
      console.log('🔵 SUBMIT - Après fetch', response);

      if (response.status === 429) {
        const data = await response.json();
        setError(`Limite quotidienne atteinte (${data.limite} exercices/jour). Passez à un abonnement supérieur pour continuer.`);
        return;
      }

      const data = await response.json();
      console.log('🔵 SUBMIT - Réponse API:', data); // ← AJOUTE CE LOG

      if (data.success) {
        setResult({
          correct: data.correct,
          correctAnswer: data.reponseCorrecte,
        });
        console.log('🔵 SUBMIT - setResult OK', {
          correct: data.correct,
          correctAnswer: data.reponseCorrecte,
        });

        setStats((prev) => ({
          correct: prev.correct + (data.correct ? 1 : 0),
          incorrect: prev.incorrect + (data.correct ? 0 : 1),
          total: prev.total + 1,
        }));
      }
    } catch (err) {
      console.error('Erreur vérification:', err);
      setError('Erreur lors de la vérification');
    } finally {
      setIsChecking(false);
    }
  };

  // Fonction pour insérer un caractère spécial - utilise la factory du module utilitaire
  const insertSpecialChar = createInsertSpecialChar(inputRef, userAnswer, setUserAnswer);

  // Fonction pour démarrer l'exercice et scroller
  const startExercise = () => {
    setShowExercise(true);
    // Toujours charger une nouvelle question au démarrage
    loadNewQuestion();
    // Scroll vers l'exercice après un court délai
    setTimeout(() => {
      exerciseRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 100);
  };

  // Fonction pour revenir aux paramètres
  const backToSettings = () => {
    setShowExercise(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Calculer le pourcentage de réussite
  const successRate = stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0;

  // Fonction pour charger l'explication du temps actuel
  const loadTenseExplanation = async () => {
    if (loadingExplanation || !question) return;

    setLoadingExplanation(true);

    try {
      const response = await fetch(`/api/exercices/explication-temps?mode=${question.mode}&temps=${question.temps}&langueCible=${question.langueCible}&langueExplication=${langueInterface}`);

      if (!response.ok) {
        throw new Error('Erreur API');
      }

      const data = await response.json();

      if (data.success) {
        setCurrentTenseExplanation(data.data);
        setShowTenseExplanation(true);
      }
    } catch (error) {
      console.error('❌ Erreur:', error);
    } finally {
      setLoadingExplanation(false);
    }
  };

  // Fonction pour charger les conjugaisons du verbe actuel
  const loadConjugations = async () => {
    if (loadingConjugations || !question) return;

    setLoadingConjugations(true);

    try {
      const response = await fetch(
        `/api/exercices/conjugaisons-verbe?verbeId=${question.verbeId}&langue=${question.langueCible}&mode=${question.mode}&temps=${question.temps}`
      );

      if (!response.ok) throw new Error('Erreur API');

      const data = await response.json();

      if (data.success) {
        setCurrentConjugations(data);
        setShowConjugations(true);
      }
    } catch (error) {
      console.error('❌ Erreur conjugaisons:', error);
    } finally {
      setLoadingConjugations(false);
    }
  };

  // Charger les infos du verbe (sans ouvrir la modale)
  const loadVerbInfoSilent = async () => {
    if (loadingVerbInfo || !question) return;
    setLoadingVerbInfo(true);

    try {
      const response = await fetch(`/api/exercices/info-verbe?verbId=${question.verbeId}`);
      if (!response.ok) throw new Error('Erreur API');
      const data = await response.json();

      if (data.success) {
        setCurrentVerbInfo(data.data);
        // ← PAS de setShowVerbInfo(true) ici !
      }
    } catch (error) {
      console.error('❌ Erreur infos verbe:', error);
    } finally {
      setLoadingVerbInfo(false);
    }
  };

  // Charger ET afficher la modale
  const loadVerbInfo = async () => {
    await loadVerbInfoSilent();
    setShowVerbInfo(true); // Ouvre la modale seulement ici
  };

  return (
    <div className="conjugation-exercise">
      {/* ÉCRAN 1 : PARAMÈTRES */}
      {!showExercise && (
        <div className="settings-screen">
          {/* Header */}
          {!isEmbed && (
            <div className="exercise-header">
              <div className="header-title">
                <Dumbbell size={32} color="#4F46E5" />
                <h2>{t('conjugation.title')}</h2>
              </div>
            </div>
          )}

          {/* Paramètres */}
          <div className="settings-container">
            <h3 style={{ textAlign: 'center', marginBottom: '24px' }}>{t('conjugation.chooseParams')}</h3>

            {/* Filtre de langue */}
            <div className="setting-item">
              <label>{t('conjugation.langueExercice')}</label>
              <select ref={languageSelectRef} value={languageFilter} onChange={(e) => setLanguageFilter(e.target.value)} className="language-select">
                {getLanguageOptions(t).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.flag} {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Sélecteur de mode de difficulté */}
            <div className="setting-item">
              <label>{t('conjugation.modeDifficulte')}</label>
              <select value={difficultyMode} onChange={(e) => setDifficultyMode(e.target.value)} className="language-select">
                <option value="strict">{t('conjugation.modeStrict')}</option>
                <option value="normal">{t('conjugation.modeNormal')}</option>
                <option value="challenge">{t('conjugation.modeChallenge')}</option>
              </select>
            </div>

            {/* Filtre de temps */}
            <div className="setting-item">
              <label>{t('conjugation.tempsATravailler')}</label>
              <select ref={tenseSelectRef} value={tenseFilter} onChange={(e) => setTenseFilter(e.target.value)} className="language-select">
                {tenseOptions.map((option, index) => (
                  <option key={option.value === 'random' ? 'random' : `${option.langue}-${option.mode}-${option.value}-${index}`} value={option.value}>
                    {option.value === 'random' ? `⏰ ${option.label}` : `⏰ ${option.label} [Niv. ${option.niveau}]`}
                  </option>
                ))}
              </select>
            </div>

            {/* Sélecteur de type de verbe */}
            <div className="setting-item">
              <label>{t('conjugation.typeVerbe')}</label>
              <select value={verbTypeFilter} onChange={(e) => setVerbTypeFilter(e.target.value)} className="language-select">
                {(getVerbTypeOptions(languageFilter) || []).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Badge niveau utilisateur */}
            {userNiveau >= 0 && (
              <div className="setting-item" style={{ textAlign: 'center' }}>
                <div className="user-level-badge" style={{ display: 'inline-block' }}>
                  {getLangueFlag(languageFilter === 'all' ? 'fr' : languageFilter)} {t('conjugation.votreNiveau')} : {userNiveau}
                </div>
              </div>
            )}

            {/* Bouton Commencer */}
            <div style={{ textAlign: 'center', marginTop: '32px' }}>
              <button onClick={startExercise} className="btn-start-exercise">
                <Dumbbell size={24} />
                {t('conjugation.start')}
              </button>
            </div>
          </div>
        </div> // settings-screen
      )}

      {/* ÉCRAN 2 : EXERCICE */}
      {showExercise && (
        <>
          {/* Ancre pour l'exercice */}
          <div ref={exerciseRef}></div>

          {/* Afficher l'exercice seulement si showExercise est true */}
          {showExercise && (
            <>
              {/* Message d'erreur */}
              {error && (
                <div className="exercise-error">
                  <XCircle size={20} />
                  <div>
                    <p className="error-title">{t('conjugation.erreur')}</p>
                    <p className="error-message">{error}</p>
                    {error.includes('Aucun verbe') && <p className="error-hint">{t('conjugation.erreurHintEnrichissement')}</p>}
                  </div>
                </div>
              )}

              {/* Chargement */}
              {loading && !error && (
                <div className="exercise-loading">
                  <Loader2 size={48} className="spinning" color="#4F46E5" />
                  <p>{t('conjugation.chargementQuestion')}</p>
                </div>
              )}

              {/* Question */}
              {!loading && !error && question && (
                <div className="exercise-card">
                  {/* Boutons d'aide */}
                  <div className="help-buttons">
                    {/* Bouton 1 : Explication du temps */}
                    <button onClick={loadTenseExplanation} className="btn-explanation" disabled={!question || loadingExplanation} title={t('conjugation.explicationTemps')}>
                      {loadingExplanation ? (
                        <>
                          <Loader2 size={20} className="spinning" />
                          {t('conjugation.chargement')}
                        </>
                      ) : (
                        <>
                          <BookOpen size={20} />
                          {t('conjugation.explicationTemps')}
                        </>
                      )}
                    </button>

                    {/* Bouton 2 : Voir les conjugaisons */}
                    <button onClick={loadConjugations} className="btn-explanation" disabled={!question || loadingConjugations} title={t('conjugation.voirConjugaisons')}>
                      {loadingConjugations ? (
                        <>
                          <Loader2 size={16} className="spinning" />
                          {t('conjugation.chargement')}
                        </>
                      ) : (
                        <>
                          <List size={16} />
                          {t('conjugation.voirConjugaisons')}
                        </>
                      )}
                    </button>

                    {/* Bouton 3 : À propos du verbe */}
                    <button
                      onClick={loadVerbInfo}
                      className={`btn-explanation ${currentVerbInfo && isVerbIrregular(currentVerbInfo) ? 'irregular' : ''}`}
                      disabled={!question || loadingVerbInfo}
                      title={t('conjugation.aProposVerbe')}
                    >
                      {loadingVerbInfo ? (
                        <>
                          <Loader2 size={16} className="spinning" />
                          {t('conjugation.chargement')}
                        </>
                      ) : (
                        <>
                          <Info size={16} />{t('conjugation.aProposVerbe')}
                        </>
                      )}
                    </button>
                  </div>

                  {/* Consigne compacte - 3 lignes */}
                  <div className="question-compact">
                    <div className="verb-info-block">
                      <div className="verb-line">
                        <span className="verb-flag">{question.langueCible === 'fr' ? '🇫🇷' : question.langueCible === 'en' ? '🇬🇧' : '🇪🇸'}</span>
                        <span className="verb-target">{question.infinitifCible.toUpperCase()}</span>
                        {question.infinitifFr && <span className="verb-fr-hint"> ({question.infinitifFr})</span>}
                      </div>
                      <div className="info-line">
                        <span className="info-label">{t('conjugation.temps')}:</span>
                        <span className="info-value">{question.tempsLabel}</span>
                      </div>
                      <div className="info-line">
                        <span className="info-label">{t('conjugation.personne')}:</span>
                        <span className="info-value">{question.personne}</span>
                      </div>
                    </div>
                  </div>

                  {/* Formulaire de réponse */}
                  {!result && (
                    <form onSubmit={handleSubmit} className="answer-form">
                      {/* Clavier virtuel avec checkbox "Vérifier les accents" */}
                      <VirtualKeyboard
                        langue={question.langueCible}
                        checkAccents={checkAccents}
                        onCheckAccentsChange={setCheckAccents}
                        onCharClick={insertSpecialChar}
                      />

                      <div className="answer-input-group">
                        <input
                          ref={inputRef}
                          type="text"
                          value={userAnswer}
                          onChange={(e) => setUserAnswer(e.target.value)}
                          placeholder={t('conjugation.votreReponsePlaceholder')}
                          className="answer-input"
                          disabled={isChecking}
                          autoCapitalize="off"
                          autoCorrect="off"
                          spellCheck="false"
                          autoFocus={window.innerWidth > 768}
                        />
                      </div>

                      <button type="submit" className="btn-verify" disabled={!userAnswer.trim() || isChecking}>
                        {isChecking ? (
                          <>
                            <Loader2 size={20} className="spinning" />
                            {t('conjugation.verification')}
                          </>
                        ) : (
                          <>
                            <CheckCircle size={20} />
                            {t('conjugation.verifierReponse')}
                          </>
                        )}
                      </button>

                      {/* Bouton passer à un autre verbe */}
                      {(!result || result.correct) && (
                        <button type="button" onClick={loadNewQuestion} className="btn-verify">
                          <RotateCw size={20} />
                          {t('conjugation.passerAutreVerbe')}
                        </button>
                      )}
                    </form>
                  )}

                  {/* Résultat */}
                  {result && (
                    <>
                      <div className={`result-box ${result.correct ? 'correct' : 'incorrect'}`}>
                        <div className="result-icon">{result.correct ? <CheckCircle size={48} color="#10B981" /> : <XCircle size={48} color="#EF4444" />}</div>
                        <div className="result-content">
                          <h3 className="result-title">{result.correct ? t('conjugation.correct') : t('conjugation.incorrect')}</h3>
                          {!result.correct && (
                            <div className="result-details">
                              <p className="your-answer">
                                {t('conjugation.votreReponse')} : <span className="wrong">{userAnswer}</span>
                              </p>
                              <p className="correct-answer">
                                {t('conjugation.reponseCorrecte')} : <span className="right">{result.correctAnswer}</span>
                              </p>
                            </div>
                          )}
                          {result.correct && (
                            <p className="result-message">
                              {t('conjugation.laReponseEstBien')} : <strong>{result.correctAnswer}</strong>
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Stats compactes */}
                      {stats.total > 0 && (
                        <div className="stats-compact">
                          <span className="stat-compact">
                            <CheckCircle size={16} color="#10B981" />
                            {stats.correct}
                          </span>
                          <span className="stat-compact">
                            <XCircle size={16} color="#EF4444" />
                            {stats.incorrect}
                          </span>
                          <span className="stat-compact">
                            <TrendingUp size={16} color="#4F46E5" />
                            {successRate}%
                          </span>
                        </div>
                      )}

                      <div className="result-actions">
                        {!result.correct && (
                          <button onClick={retryCurrentVerb} className="btn-retry">
                            <RotateCw size={20} />
                            {t('conjugation.retesterVerbe')}
                          </button>
                        )}
                        <button onClick={loadNewQuestion} className="btn-next">
                          <RotateCw size={20} />
                          {t('conjugation.passerAutreVerbe')}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Bouton pour revenir aux paramètres (seulement si exercice affiché) */}
              {showExercise && (
                <div style={{ textAlign: 'center', marginTop: '24px' }}>
                  <button onClick={backToSettings} className="btn-back-settings">
                    {t('conjugation.modifierParametres')}
                  </button>
                </div>
              )}
            </>
          )}

          {/* MODALE : Explication du temps */}
          {showTenseExplanation && currentTenseExplanation && (
            <div className="modal-overlay" onClick={() => setShowTenseExplanation(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>
                    <BookOpen size={24} color="#4F46E5" />
                    {currentTenseExplanation.title}
                  </h3>
                  <button onClick={() => setShowTenseExplanation(false)} className="modal-close">
                    <X size={24} />
                  </button>
                </div>

                <div className="modal-body">
                  {/* Description générale */}
                  <div className="explanation-section">
                    <h4>{t('conjugation.descriptionSection')}</h4>
                    <p className="modal-description">{currentTenseExplanation.description}</p>
                  </div>

                  {/* Descriptif du mode (si disponible) */}
                  {currentTenseExplanation.descriptifMode && (
                    <div className="explanation-section">
                      <h4>{t('conjugation.modeSection')}</h4>
                      <p className="modal-description">{currentTenseExplanation.descriptifMode}</p>
                    </div>
                  )}

                  {/* Descriptif du temps (si disponible) */}
                  {currentTenseExplanation.descriptifTemps && (
                    <div className="explanation-section">
                      <h4>{t('conjugation.tempsSection')}</h4>
                      <p className="modal-description">{currentTenseExplanation.descriptifTemps}</p>
                    </div>
                  )}

                  {/* Usage */}
                  <div className="explanation-section">
                    <h4>{t('conjugation.utilisationSection')}</h4>
                    <p className="modal-usage">{currentTenseExplanation.usage}</p>
                  </div>

                  {/* Exemples */}
                  {currentTenseExplanation.examples && currentTenseExplanation.examples.length > 0 && (
                    <div className="explanation-section">
                      <h4>{t('conjugation.exemplesSection')}</h4>
                      <ul className="modal-examples">
                        {currentTenseExplanation.examples.map((example, index) => (
                          <li key={index}>{example}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* MODALE : Conjugaisons complètes */}
          {showConjugations && currentConjugations && (
            <div className="modal-overlay" onClick={() => setShowConjugations(false)}>
              <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>
                    <List size={24} color="#4F46E5" />
                    {t('conjugation.conjugaisons')} : {currentConjugations.infinitif}
                  </h3>
                  <button onClick={() => setShowConjugations(false)} className="modal-close">
                    <X size={24} />
                  </button>
                </div>

                <div className="modal-body">
                  {/* Info sur le temps */}
                  <div className="conjugation-info">
                    <p>
                      <strong>{t('conjugation.mode')} :</strong> {currentConjugations.mode}
                    </p>
                    <p>
                      <strong>{t('conjugation.temps')} :</strong> {currentConjugations.temps}
                    </p>
                  </div>

                  {/* Note pédagogique si présente */}
                  {currentConjugations.note && <div className="conjugation-note">ℹ️ {currentConjugations.note}</div>}

                  {/* Tableau des conjugaisons */}
                  <div className="conjugation-table">
                    {currentConjugations.conjugaisons.map((item, index) => (
                      <div key={index} className={`conjugation-row ${!item.forme || item.forme === '-' ? 'empty' : ''}`}>
                        <span className="pronoun-col">{item.pronoun}</span>
                        <span className="form-col">{item.forme && item.forme !== '-' ? item.forme : t('conjugation.nonUtilise')}</span>
                      </div>
                    ))}
                  </div>

                  {/* Astuce pédagogique */}
                  <div className="conjugation-tip">
                    {t('conjugation.astuce')}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* MODALE : Informations sur le verbe */}
          {showVerbInfo && currentVerbInfo && (
            <div className="modal-overlay" onClick={() => setShowVerbInfo(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>
                    <Info size={24} color="#4F46E5" />{t('conjugation.aProposDe')} : {currentVerbInfo.infinitif}
                  </h3>
                  <button onClick={() => setShowVerbInfo(false)} className="modal-close">
                    <X size={24} />
                  </button>
                </div>

                <div className="modal-body">
                  {currentVerbInfo.attributes.length > 0 ? (
                    <div className="verb-attributes">
                      {currentVerbInfo.attributes.map((attr, index) => (
                        <div key={index} className={`attribute-item ${attr.important ? 'important' : ''}`}>
                          <div className="attribute-icon">{attr.icon}</div>
                          <div className="attribute-content">
                            <div className="attribute-label">{attr.label}</div>
                            <div className="attribute-value">{attr.value}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-attributes">{t('conjugation.pasDeCaracteristiques')}</p>
                  )}

                  <div className="verb-info-tip">
                    {t('conjugation.bonASavoir')}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
